import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

import { getAuthStore } from "~/modules/auth/auth-store";
import { loginCredentials } from "~/modules/auth/credentials";
import { authorizeMcpSession } from "~/modules/cap-code/network";

export const Route = createFileRoute("/auth/cap-code-mcp")({
  component: RouteComponent,
  validateSearch: z.object({
    client_id: z.string(),
    redirect_uri: z.string(),
    state: z.string().optional(),
    scope: z.string().optional(),
    resource: z.string().optional(),
    code_challenge: z.string(),
    code_challenge_method: z.literal("S256"),
  }),
});

function loginRedirect() {
  const redirectTo = `${window.location.pathname}${window.location.search}`;
  window.location.href = `/?redirectTo=${encodeURIComponent(redirectTo)}`;
}

function RouteComponent() {
  const search = Route.useSearch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function authorize() {
      const credentials = loginCredentials.get();
      if (!credentials) {
        loginRedirect();
        return;
      }

      const accessToken = await getAuthStore().getAccessToken();
      const refreshedCredentials = loginCredentials.get();
      if (!accessToken || !refreshedCredentials?.refreshToken) {
        loginRedirect();
        return;
      }

      const response = await authorizeMcpSession({
        clientId: search.client_id,
        redirectUri: search.redirect_uri,
        state: search.state,
        scope: search.scope,
        resource: search.resource,
        codeChallenge: search.code_challenge,
        codeChallengeMethod: search.code_challenge_method,
        refreshToken: refreshedCredentials.refreshToken,
      });

      if (!cancelled) {
        window.location.href = response.redirectUrl;
      }
    }

    authorize().catch((cause) => {
      if (!cancelled) {
        setError(cause instanceof Error ? cause.message : String(cause));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    search.client_id,
    search.code_challenge,
    search.code_challenge_method,
    search.redirect_uri,
    search.resource,
    search.scope,
    search.state,
  ]);

  return (
    <main className="p-4">
      <h1 className="text-xl font-semibold">Connecting MCP client to CapCode</h1>
      <p className="text-muted-foreground mt-2">
        {error ? `Could not connect: ${error}` : "Finishing authentication..."}
      </p>
    </main>
  );
}
